import React, { useId, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { twMerge } from "tailwind-merge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import FilterWrapper from "./FilterWrapper";

type Option = { label: string; value: string };

type Props = {
  icon: React.ReactNode;
  title: string;
  /** Selected option value, or undefined/"ANY" for no restriction. */
  value: string | undefined;
  setValue: (value: string | undefined) => void;
  options: Option[];
  anyLabel: string;
  searchPlaceholder: string;
};

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLocaleLowerCase()
    .trim();

/** Searchable single select for long option lists (cities, provinces). */
const FilterCombobox: React.FC<Props> = ({
  icon,
  title,
  value,
  setValue,
  options,
  anyLabel,
  searchPlaceholder,
}) => {
  const t = useTranslations("search");
  const id = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const selected = useMemo(
    () =>
      value && value !== "ANY"
        ? options.find(
            (option) =>
              option.value.toLocaleLowerCase() === value.toLocaleLowerCase(),
          )
        : undefined,
    [options, value],
  );

  const results = useMemo(() => {
    const q = normalize(query);
    if (!q) return options;
    // Prefix matches first, then matches anywhere in the name.
    const starts: Option[] = [];
    const contains: Option[] = [];
    for (const option of options) {
      const label = normalize(option.label);
      if (label.startsWith(q)) starts.push(option);
      else if (label.includes(q) || normalize(option.value).includes(q))
        contains.push(option);
    }
    return [...starts, ...contains];
  }, [options, query]);

  const choose = (next: string | undefined) => {
    setValue(next);
    setOpen(false);
    setQuery("");
  };

  const moveActive = (next: number) => {
    const clamped = Math.max(0, Math.min(results.length - 1, next));
    setActive(clamped);
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${clamped}"]`)
      ?.scrollIntoView({ block: "nearest" });
  };

  return (
    <FilterWrapper
      icon={icon}
      title={title}
      clear={() => setValue(undefined)}
      showClear={!!selected}
    >
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery("");
          setActive(0);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${title}: ${selected?.label ?? anyLabel}`}
            className={twMerge(
              "flex h-11 w-full min-w-0 items-center gap-2 rounded-xl border border-input bg-background px-3 text-left text-sm transition-colors hover:border-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent data-[state=open]:border-primary",
              selected && "border-primary font-semibold",
            )}
          >
            <Search className="size-4 flex-none text-muted-foreground" />
            <span
              className={twMerge(
                "min-w-0 flex-1 truncate",
                !selected && "text-muted-foreground",
              )}
            >
              {selected?.label ?? anyLabel}
            </span>
            <ChevronsUpDown className="size-4 flex-none text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[max(16rem,var(--radix-popover-trigger-width))] rounded-2xl p-0"
          onOpenAutoFocus={(event) => {
            // Focus the search field rather than the first option.
            event.preventDefault();
            (
              event.currentTarget as HTMLElement | null
            )?.querySelector<HTMLInputElement>("input")?.focus();
          }}
        >
          <div className="relative border-b border-border p-2">
            <Search
              className="pointer-events-none absolute left-5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              type="search"
              role="combobox"
              aria-expanded
              aria-controls={`${id}-list`}
              aria-activedescendant={
                results[active] ? `${id}-option-${active}` : undefined
              }
              aria-label={searchPlaceholder}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  moveActive(active + 1);
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  moveActive(active - 1);
                } else if (event.key === "Enter") {
                  event.preventDefault();
                  if (results[active]) choose(results[active].value);
                }
              }}
              placeholder={searchPlaceholder}
              className="h-10 rounded-xl pl-9 text-base sm:text-sm"
            />
          </div>
          {selected && (
            <div className="border-b border-border p-1.5">
              <button
                type="button"
                onClick={() => choose(undefined)}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
                {anyLabel}
              </button>
            </div>
          )}
          <ul
            ref={listRef}
            id={`${id}-list`}
            role="listbox"
            aria-label={title}
            className="max-h-72 overflow-y-auto overscroll-contain p-1.5"
          >
            {results.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t("combobox.empty")}
              </li>
            )}
            {results.map((option, index) => {
              const isSelected = option.value === selected?.value;
              return (
                <li
                  key={option.value}
                  id={`${id}-option-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => choose(option.value)}
                  onMouseMove={() => setActive(index)}
                  className={twMerge(
                    "flex cursor-pointer items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm",
                    index === active && "bg-muted",
                    isSelected && "font-semibold",
                  )}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && (
                    <Check className="size-4 flex-none text-foreground" />
                  )}
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    </FilterWrapper>
  );
};

export default FilterCombobox;
